import { useEffect, useRef, useState } from "react";
import { ArrowRight, Loader2, LockKeyhole } from "lucide-react";
import { verifyTrackingAccess } from "./api";
import sclLogoUrl from "./assets/scl-logo.png";
import "./access-gate.css";

export function AccessGate({ token, onVerified }: { token: string; onVerified: () => void }) {
  const [digits, setDigits] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const pending = useRef(false);
  useEffect(() => { document.title = "Отслеживание · SCL"; }, []);
  useEffect(() => {
    if (!wait) return;
    const timer = window.setTimeout(() => setWait((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [wait]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (pending.current || wait || !/^[0-9]{4}$/.test(digits)) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await verifyTrackingAccess(token, digits);
      setDigits("");
      onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось проверить доступ. Попробуйте позже.");
      if (err && typeof err === "object" && "retryAfter" in err) setWait(Number(err.retryAfter) || 0);
      setDigits("");
      input.current?.focus();
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  return <main className="tracking-access">
    <img className="tracking-access-logo" src={sclLogoUrl} alt="SCL by DR.Mult" />
    <section aria-labelledby="tracking-access-title">
      <LockKeyhole size={28} aria-hidden="true" />
      <h1 id="tracking-access-title">Отслеживание отправления</h1>
      <form onSubmit={submit} aria-busy={busy}>
        <label htmlFor="tracking-phone-suffix">Последние 4 цифры телефона получателя</label>
        <input ref={input} id="tracking-phone-suffix" type="text" inputMode="numeric"
          autoComplete="off" maxLength={4} pattern="[0-9]{4}" required
          value={digits} onChange={(event) => setDigits(event.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
          readOnly={busy} aria-invalid={!!error} aria-describedby={error ? "tracking-access-error" : undefined} />
        {error && <p id="tracking-access-error" role="alert">{error}</p>}
        <button className="button" type="submit" disabled={busy || wait > 0 || digits.length !== 4}>
          {busy ? <Loader2 className="access-spin" size={20} aria-hidden="true" /> : <ArrowRight size={20} aria-hidden="true" />}
          {busy ? "Проверяем…" : wait ? `Повторить через ${wait} с` : "Открыть отправление"}
        </button>
      </form>
    </section>
  </main>;
}
